import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { Tree } from 'primereact/tree'
import { classNames } from 'primereact/utils'
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { useGetHeadlineDataProjectRun } from '../../api/report-API'
import { useGetUsers } from '../../api/user-API'
import { protoService } from '../../proto/ProtoService'
import { currentTeamState, currentUserState, vehicleListState } from '../../utils/state-atoms'
import { CheckboxStyle } from '../../utils/styling-constants'
import {
  Areas,
  IndyCarAreas,
  IndyCarTeams,
  NASCAR28Areas,
  NASCAR28Teams,
  NASCARAreas,
  NASCARTeams
} from '../../utils/constants'

const PROJECT_SELECTION_STORAGE_KEY = 'projectRunSelection'
const STATE_SELECTION_STORAGE_KEY = 'headlineStateSelection'
const BASELINE_SELECTION_STORAGE_KEY = 'headlineBaselineSelection'
export const PLOT_PROJECT_RUN_SELECTION_KEY = 'PlotProjectRunSelection'
export const PLOT_FILTER_SELECTION_KEY = `${PLOT_PROJECT_RUN_SELECTION_KEY}:filters`
const KEY_DELIMITER = '::'
const oneLevelTreeStyle = {
  root: {
    className:
      '!bg-transparent !p-0 asis-0 grow max-h-[calc(50% - 30px)] overflow-y-auto no-toggler'
  },
  content: {
    className: '!p-0'
  },
  node: {
    className: '!p-0'
  },
  // Preset sizes the checkbox root at w-6 while App.css shrinks the box to 18px,
  // leaving dead space before the label.
  nodeCheckbox: {
    className: '!w-[18px] mr-1'
  },
  label: {
    className: 'text-xs'
  },
  input: {
    className: 'w-4 h-4'
  },
  container: {
    className: 'overflow-y-auto h-full'
  }
}

type ProjectRunSelectorProps = {
  loadingHeadlineData: boolean
  showFilter: boolean
  showCarFilter?: boolean
  showAreaFilter?: boolean
  storageScope?: string
  initialProjectRunSelection?: Record<string, any> | null
  initialRunId?: string
  initialCaseId?: string
  initialStates?: string[]
  initialBaselines?: string[]
  topSpacing?: number
  baselineSourceData?: Record<string, any>[]
  filterContent?: ReactNode
  onProjectRunSelectionChanged: (
    projectRunSelection: Record<string, boolean>,
    projects: string[],
    projectSelectionChanged: boolean,
    runs: string[],
    runSelectionChanged: boolean,
    newSelectedProjects: string[]
  ) => void
  onBaselineSelectionChanged: (baselines: string[], reload: boolean) => void
  // if reload is true, parent component should rebuild the data
  onStateSelectionChanged: (states: string[], reload: boolean) => void
  onFilterLoadingChanged?: (loading: boolean) => void
  onReset?: () => void
  onProjectRunLoaded?: (cases: Record<string, any>[]) => void
  onInitialProjectRunSelectionRejected?: (
    selectionType?: 'runId' | 'caseId',
    value?: string
  ) => void
}

type TreeNode = {
  key: string
  label: string
  name?: string
  desc?: string
  children?: TreeNode[]
}

const togglerTemplate = (node, options) => {
  if (!node) {
    return
  }

  const expanded = options.expanded
  const iconClassName = classNames('p-tree-toggler-icon pi pi-fw text-xs pr-1', {
    'pi-chevron-right': !expanded && node.children,
    'pi-chevron-down': expanded && node.children
  })

  return (
    <button type="button" className="p-tree-toggler p-link" tabIndex={-1} onClick={options.onClick}>
      <span className={iconClassName} aria-hidden="true"></span>
    </button>
  )
}

// Long project/run descriptions are clipped to one line so the checkbox column stays aligned.
const projectRunNodeTemplate = (node: TreeNode) => {
  if (!node) {
    return null
  }

  return (
    <span className="block truncate" title={node.label}>
      {node.name ?? node.label}
      {node.desc && <span className="text-neutral-500 dark:text-neutral-400"> ({node.desc})</span>}
    </span>
  )
}

const ProjectRunSelector: FC<ProjectRunSelectorProps> = ({
  loadingHeadlineData,
  storageScope,
  initialProjectRunSelection = null,
  initialRunId = '',
  initialCaseId = '',
  initialStates = [],
  initialBaselines = [],
  baselineSourceData = [],
  topSpacing = 0,
  filterContent,
  onProjectRunSelectionChanged,
  onStateSelectionChanged,
  onBaselineSelectionChanged,
  onFilterLoadingChanged,
  onReset,
  onProjectRunLoaded,
  onInitialProjectRunSelectionRejected,
  showFilter,
  showAreaFilter,
  showCarFilter
}: ProjectRunSelectorProps) => {
  const [projectRunNodes, setProjectRunNodes] = useState<TreeNode[]>([])
  const [stateNodes, setStateNodes] = useState<TreeNode[]>([])
  const [selectedProjectRunKeys, setSelectedProjectRunKeys] = useState(null)
  const [expandedProjectRunKeys, setExpandedProjectRunKeys] = useState<Record<string, boolean>>({})
  const [selectedStateKeys, setSelectedStateKeys] = useState(() =>
    initialStates.length > 0
      ? initialStates.reduce((acc: Record<string, { checked: boolean }>, state) => {
          acc[String(state)] = { checked: true }
          return acc
        }, {})
      : null
  )
  const [baselineNodes, setBaselineNodes] = useState<TreeNode[]>([])
  const [selectedBaselineKeys, setSelectedBaselineKeys] = useState(() =>
    initialBaselines.length > 0
      ? initialBaselines.reduce((acc: Record<string, { checked: boolean }>, baseline) => {
          acc[String(baseline)] = { checked: true }
          return acc
        }, {})
      : null
  )
  const [byMeOnly, setByMeOnly] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [selectedArea, setSelectedArea] = useState(null)
  const [selectedCar, setSelectedCar] = useState(null)
  const [userOptions, setUserOptions] = useState([])
  const [{ loading: projectRunLoading }, getProjectRun] = useGetHeadlineDataProjectRun()
  const refRawProjectRunData = useRef<any[]>([])
  const currentTeam = useRecoilValue(currentTeamState)
  const currentUser = useRecoilValue(currentUserState)
  const refFirstLoad = useRef(true)
  const refInitialProjectRunSelection = useRef(initialProjectRunSelection)
  const refInitialRunId = useRef(initialRunId)
  const refInitialCaseId = useRef(initialCaseId)
  const refUrlOwnedStateSelection = useRef(false)
  const [{ loading: loadingUsers }, getUsers] = useGetUsers()
  const vehicles = useRecoilValue(vehicleListState)

  const treeHeightClass = {
    maxHeight: `calc(33% - ${showFilter ? 52 : 6}px)`,
    minHeight: `calc(33% - ${showFilter ? 52 : 6}px)`
  }

  const areas = useMemo(() => {
    return IndyCarTeams.includes(currentTeam)
      ? IndyCarAreas
      : NASCAR28Teams.includes(currentTeam)
        ? NASCAR28Areas
        : NASCARTeams.includes(currentTeam)
          ? NASCARAreas
          : Areas
  }, [currentTeam])

  const getStorageKey = (baseKey: string) => {
    const teamScope = storageScope || currentTeam || 'unknown-team'
    return `${baseKey}:${teamScope}`
  }

  const readSelectionFromStorage = (storageKey: string) => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) {
        return {}
      }
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (error) {
      console.error(`Failed to parse selection for key ${storageKey}:`, error)
      return {}
    }
  }

  const filterSelectionByAllowedKeys = (
    selection: Record<string, any>,
    allowedKeys: Set<string>
  ) => {
    return Object.entries(selection).reduce(
      (acc, [key, value]) => {
        if (allowedKeys.has(key) && (value?.checked || value?.partialChecked)) {
          acc[key] = { checked: value?.checked, partialChecked: value?.partialChecked }
        }
        return acc
      },
      {} as Record<string, { checked: boolean; partialChecked?: boolean }>
    )
  }

  const areSelectionsEqual = (left: Record<string, any> | null, right: Record<string, any>) => {
    const normalize = (selection: Record<string, any> | null) => {
      return Object.entries(selection || {})
        .filter(([, value]) => value?.checked || value?.partialChecked)
        .map(
          ([key, value]) => `${key}:${Boolean(value?.checked)}:${Boolean(value?.partialChecked)}`
        )
        .sort()
        .join('|')
    }

    return normalize(left) === normalize(right)
  }

  const handleShowMeOnlyChange = (evt: CheckboxChangeEvent) => {
    setByMeOnly(evt.checked as boolean)
    setSelectedUserId(currentUser._id)
  }

  const handleProjectRunSelectionChange = e => {
    const nextProjectRunSelection = e.value || {}
    const shouldClearUrlOwnedStateSelection = !!e.originalEvent && refUrlOwnedStateSelection.current
    const shouldIgnorePersistedStateSelection =
      shouldClearUrlOwnedStateSelection || !!e.ignorePersistedStateSelection
    setSelectedProjectRunKeys(nextProjectRunSelection)

    const nextExpandedSelection: Record<string, boolean> = {}
    Object.keys(nextProjectRunSelection).forEach(key => {
      if (key.includes(KEY_DELIMITER)) {
        nextExpandedSelection[key.split(KEY_DELIMITER)[0]] = true
      } else {
        nextExpandedSelection[key] = true
      }
    })
    setExpandedProjectRunKeys(prev => ({ ...prev, ...nextExpandedSelection }))

    const allSelectedProjects: Set<string> = new Set()
    const allSelectedRuns: Set<string> = new Set()
    const states: Set<string> = new Set()

    // find all selected projects and states associated with selected projects/runs
    Object.entries(nextProjectRunSelection).forEach(([key, value]: [string, any]) => {
      if (value.checked) {
        if (key.includes(KEY_DELIMITER)) {
          // run node
          const parts = key.split(KEY_DELIMITER)
          const projectKey = parts[0]
          const runKey = parts[1]
          // find states for this run from raw data
          refRawProjectRunData.current.forEach(row => {
            if (row[0] === projectKey && row[1] === runKey) {
              states.add(row[2])
            }
          })
          allSelectedProjects.add(projectKey)
          allSelectedRuns.add(key)
        } else {
          // project node
          allSelectedProjects.add(key)
        }
      }
    })

    // make state tree nodes
    const stateNodeMap: Record<string, TreeNode> = {}
    states.forEach(state => {
      stateNodeMap[state] = {
        key: state,
        label: state
      }
    })
    const availableStates = Object.values(stateNodeMap)
    setStateNodes(availableStates)
    const availableStateSet = new Set(availableStates.map(node => node.key))
    const persistedStateMap = readSelectionFromStorage(getStorageKey(STATE_SELECTION_STORAGE_KEY))
    const selectedStateMap =
      !shouldClearUrlOwnedStateSelection && Object.keys((selectedStateKeys as any) || {}).length > 0
        ? (selectedStateKeys as any) || {}
        : shouldIgnorePersistedStateSelection
          ? {}
          : persistedStateMap
    const prevSelectedStates = Object.entries(selectedStateMap)
      .filter(([, value]: [string, any]) => value?.checked)
      .map(([key]) => key)
      .sort()
    const validSelectedStates = prevSelectedStates
      .filter(state => availableStateSet.has(state))
      .sort()
    if (shouldClearUrlOwnedStateSelection) {
      refUrlOwnedStateSelection.current = false
      setSelectedStateKeys(null)
      localStorage.setItem(getStorageKey(STATE_SELECTION_STORAGE_KEY), JSON.stringify({}))
      onStateSelectionChanged([], false)
    } else if (prevSelectedStates.join('|') !== validSelectedStates.join('|')) {
      const nextSelectedStateKeys = validSelectedStates.reduce((acc, stateKey) => {
        acc[stateKey] = { checked: true }
        return acc
      }, {})
      setSelectedStateKeys(nextSelectedStateKeys)
      localStorage.setItem(
        getStorageKey(STATE_SELECTION_STORAGE_KEY),
        JSON.stringify(nextSelectedStateKeys)
      )
      onStateSelectionChanged(validSelectedStates, false)
    } else if (validSelectedStates.length > 0) {
      onStateSelectionChanged(validSelectedStates, false)
    }

    if (availableStates.length === 0) {
      setSelectedStateKeys(null)
      localStorage.setItem(getStorageKey(STATE_SELECTION_STORAGE_KEY), JSON.stringify({}))
      onStateSelectionChanged([], false)
    }

    // find the diff and notify parent to reload data if needed
    // if only run level selection changed, no need to reload data, just update the filter
    // if project level selection changed, need to reload data
    const diff = {}
    Object.entries(nextProjectRunSelection).forEach(([key, value]: [string, any]) => {
      if (selectedProjectRunKeys) {
        const prevValue = (selectedProjectRunKeys as any)[key]
        if (
          !prevValue ||
          prevValue.checked !== value.checked ||
          prevValue.partialChecked !== value.partialChecked
        ) {
          diff[key] = value
        }
      } else {
        diff[key] = value
      }
    })

    let projectSelectionChanged = false
    let runSelectionChanged = false
    const projectList: Set<string> = new Set()
    const runList = []
    Object.entries(diff).forEach(([key, value]: [string, any]) => {
      if (key.includes(KEY_DELIMITER)) {
        // run level
        value && runList.push(key)
        runSelectionChanged = true
      } else {
        // project level
        projectList.add(key)
        projectSelectionChanged = true
      }
    })

    // onProjectRunSelectionChanged(Array.from(allSelectedProjects), projectSelectionChanged, runList, runSelectionChanged, Array.from(projectList))
    onProjectRunSelectionChanged(
      nextProjectRunSelection,
      Array.from(allSelectedProjects),
      projectSelectionChanged,
      Array.from(allSelectedRuns),
      runSelectionChanged,
      Array.from(projectList)
    )

    if (!e.skipPersistProjectRunSelection) {
      localStorage.setItem(
        getStorageKey(PROJECT_SELECTION_STORAGE_KEY),
        JSON.stringify(nextProjectRunSelection)
      )
    }
  }

  const handleStateSelectionChange = e => {
    const nextStateSelection = e.value || {}
    refUrlOwnedStateSelection.current = false
    setSelectedStateKeys(nextStateSelection)

    const states: string[] = []
    Object.entries(nextStateSelection).forEach(([key, value]: [string, any]) => {
      if (value.checked) {
        states.push(key)
      }
    })

    localStorage.setItem(
      getStorageKey(STATE_SELECTION_STORAGE_KEY),
      JSON.stringify(nextStateSelection)
    )
    onStateSelectionChanged(states, false)
  }

  const handleBaselineSelectionChange = e => {
    setSelectedBaselineKeys(e.value)

    const baselines: string[] = []
    Object.entries(e.value).forEach(([key, value]: [string, any]) => {
      if (value.checked) {
        baselines.push(key)
      }
    })

    localStorage.setItem(
      getStorageKey(BASELINE_SELECTION_STORAGE_KEY),
      JSON.stringify(e.value || {})
    )
    onBaselineSelectionChanged(baselines, false)
  }

  const handleSelection = evt => {
    // may need to delay
    if (evt.node.children) {
      // project node clicked
      // need to reload
    } else {
      // run node clicked
      // need to change filter and local state filter
    }
  }

  const buildSelectAllProjectRunSelection = () => {
    const nextSelection: Record<string, { checked: boolean }> = {}

    const visitNode = (node: TreeNode) => {
      nextSelection[node.key] = { checked: true }
      node.children?.forEach(visitNode)
    }

    projectRunNodes.forEach(visitNode)
    return nextSelection
  }

  const handleSelectAllProjectRuns = () => {
    const nextSelection = buildSelectAllProjectRunSelection()
    handleProjectRunSelectionChange({ value: nextSelection })
  }

  const handleDeselectAllProjectRuns = () => {
    handleProjectRunSelectionChange({ value: {} })
  }

  const dataArrayToTreeNodes = (dataArray: any[]) => {
    const projectMap: Record<string, any> = {}
    const projectDescMap: Record<string, string> = {}
    const runDescMap: Record<string, string> = {}
    dataArray.forEach((row: any) => {
      const project = row[0]
      const run = row[1]
      const state = row[2]
      const projectDesc = row[3]
      const runDesc = row[4]
      if (!projectMap[project]) {
        projectMap[project] = {}
      }
      if (!projectMap[project][run]) {
        projectMap[project][run] = []
      }
      projectMap[project][run].push(state)
      if (projectDesc && !projectDescMap[project]) {
        projectDescMap[project] = projectDesc
      }
      const runKey = `${project}${KEY_DELIMITER}${run}`
      if (runDesc && !runDescMap[runKey]) {
        runDescMap[runKey] = runDesc
      }
    })

    const treeNodes = Object.keys(projectMap).map(project => ({
      key: project,
      label: projectDescMap[project] ? `${project} (${projectDescMap[project]})` : project,
      name: project,
      desc: projectDescMap[project],
      children: Object.keys(projectMap[project]).map(run => {
        const runKey = `${project}${KEY_DELIMITER}${run}`
        return {
          key: runKey,
          label: runDescMap[runKey] ? `${run} (${runDescMap[runKey]})` : run,
          name: run,
          desc: runDescMap[runKey]
        }
      })
    }))

    return treeNodes
  }

  const buildProjectRunKeySet = (nodes: TreeNode[]) => {
    const keys = new Set<string>()
    nodes.forEach(projectNode => {
      keys.add(projectNode.key)
      projectNode.children?.forEach(runNode => {
        keys.add(runNode.key)
      })
    })
    return keys
  }

  const normalizeCaseId = (caseId: string) => {
    return String(caseId || '')
      .trim()
      .replace(/^0-/, '')
  }

  const buildInitialSelectionFromRow = (row: any[], includeState: boolean) => {
    const project = String(row?.[0] || '')
    const run = String(row?.[1] || '')
    const state = String(row?.[2] || '')
    if (!project || !run) {
      return null
    }

    return {
      selection: {
        [project]: { partialChecked: true },
        [`${project}${KEY_DELIMITER}${run}`]: { checked: true }
      },
      states: includeState && state ? [state] : []
    }
  }

  const extractProjectRunFromBaselineRow = (row: Record<string, any>) => {
    const project = String(row?.project || '')
    const run = String(row?.run || '')
    if (project && run) {
      return { project, run }
    }

    const idParts = String(row?.id || '').split(KEY_DELIMITER)
    if (idParts.length >= 2) {
      return { project: idParts[0], run: idParts[1] }
    }

    return null
  }

  useEffect(() => {
    const checkedProjectRunSelection = Object.entries((selectedProjectRunKeys as any) || {})
      .filter(([, value]: [string, any]) => value?.checked)
      .map(([key]) => key)

    const selectedProjects = new Set(
      checkedProjectRunSelection.filter(key => !key.includes(KEY_DELIMITER)).map(key => String(key))
    )
    const selectedRuns = new Set(
      checkedProjectRunSelection.filter(key => key.includes(KEY_DELIMITER)).map(key => String(key))
    )

    const baselineSet = new Set<string>()
    baselineSourceData.forEach(row => {
      const projectRun = extractProjectRunFromBaselineRow(row)
      if (!projectRun) {
        return
      }

      const runKey = `${projectRun.project}${KEY_DELIMITER}${projectRun.run}`
      const selectedByProject = selectedProjects.has(projectRun.project)
      const selectedByRun = selectedRuns.has(runKey)

      if (!selectedByProject && !selectedByRun) {
        return
      }

      const baseline = row?.baseline_run

      if (!baseline) {
        return
      }

      baselineSet.add(String(baseline))
    })

    const baselineValues = Array.from(baselineSet).sort()

    setBaselineNodes(
      baselineValues.map(baselineValue => ({
        key: baselineValue,
        label: baselineValue
      }))
    )

    const baselineStorageKey = getStorageKey(BASELINE_SELECTION_STORAGE_KEY)
    const hasProjectRunSelection = selectedProjects.size > 0 || selectedRuns.size > 0

    if (baselineValues.length === 0) {
      if (selectedBaselineKeys !== null) {
        setSelectedBaselineKeys(null)
      }
      // Only clear persisted baseline selection when an explicit project/run selection is present
      if (hasProjectRunSelection) {
        localStorage.setItem(baselineStorageKey, JSON.stringify({}))
      }
      onBaselineSelectionChanged([], false)
      return
    }

    const persistedBaselineSelection = readSelectionFromStorage(baselineStorageKey)
    const baselineSelectionSource =
      selectedBaselineKeys === null ? persistedBaselineSelection : (selectedBaselineKeys as any)
    const allowedBaselineKeys = new Set(baselineValues)
    const validBaselineSelection = filterSelectionByAllowedKeys(
      baselineSelectionSource,
      allowedBaselineKeys
    )

    if (Object.keys(validBaselineSelection).length > 0) {
      if (!areSelectionsEqual(selectedBaselineKeys as any, validBaselineSelection)) {
        setSelectedBaselineKeys(validBaselineSelection)
      }
      localStorage.setItem(baselineStorageKey, JSON.stringify(validBaselineSelection))
      onBaselineSelectionChanged(Object.keys(validBaselineSelection), false)
      return
    }

    if (selectedBaselineKeys !== null) {
      setSelectedBaselineKeys(null)
    }
    localStorage.setItem(baselineStorageKey, JSON.stringify({}))
    onBaselineSelectionChanged([], false)
  }, [baselineSourceData, selectedProjectRunKeys, selectedBaselineKeys, currentTeam])

  const initData = async () => {
    try {
      const projectRunRes = (await getProjectRun({
        userId: selectedUserId || '',
        car: showCarFilter ? selectedCar || '' : '',
        area: showAreaFilter ? selectedArea || '' : '',
        page: 0,
        pageSize: 1000
      } as any)) as any

      onFilterLoadingChanged && onFilterLoadingChanged(true)

      // Check if response has valid data before processing
      if (!projectRunRes?.data?.result?.data_array) {
        console.error('Invalid or empty response from project/run API:', {
          hasData: !!projectRunRes?.data,
          hasResult: !!projectRunRes?.data?.result,
          hasDataArray: !!projectRunRes?.data?.result?.data_array,
          dataArrayLength: projectRunRes?.data?.result?.data_array?.length,
          fullResponse: projectRunRes
        })
        toast.error('No project data available for your team. Please contact support.')
        onFilterLoadingChanged && onFilterLoadingChanged(false)
        onProjectRunLoaded && onProjectRunLoaded([])
        return
      }

      refRawProjectRunData.current = projectRunRes.data.result.data_array.map(row => {
        return [...row]
      })

      onProjectRunLoaded && onProjectRunLoaded(refRawProjectRunData.current)

      const latestProjects: string[] = projectRunRes.data.latestProjects || []
      const projNodesData = dataArrayToTreeNodes(refRawProjectRunData.current)
      const allProjectRunKeys = buildProjectRunKeySet(projNodesData)

      const initialProjectRunSelectionToApply = refInitialProjectRunSelection.current
      refInitialProjectRunSelection.current = null
      const initialRunIdToApply = String(refInitialRunId.current || '').trim()
      const initialCaseIdToApply = String(refInitialCaseId.current || '').trim()
      refInitialRunId.current = ''
      refInitialCaseId.current = ''

      let resolvedInitialProjectRunSelection = initialProjectRunSelectionToApply
      let resolvedInitialStates = initialStates
      let resolvedInitialSelectionType: 'runId' | 'caseId' | undefined
      let resolvedInitialSelectionValue = ''

      if (!resolvedInitialProjectRunSelection && initialCaseIdToApply) {
        const normalizedInitialCaseId = normalizeCaseId(initialCaseIdToApply)
        const caseRow = refRawProjectRunData.current.find(
          row => normalizeCaseId(String(row?.[5] || '')) === normalizedInitialCaseId
        )
        const resolvedCaseSelection = caseRow ? buildInitialSelectionFromRow(caseRow, true) : null
        resolvedInitialProjectRunSelection = resolvedCaseSelection?.selection || null
        resolvedInitialStates = resolvedCaseSelection?.states || []
        refUrlOwnedStateSelection.current = resolvedInitialStates.length > 0
        resolvedInitialSelectionType = 'caseId'
        resolvedInitialSelectionValue = initialCaseIdToApply
      } else if (!resolvedInitialProjectRunSelection && initialRunIdToApply) {
        const runRow = refRawProjectRunData.current.find(
          row => String(row?.[1] || '') === initialRunIdToApply
        )
        const resolvedRunSelection = runRow ? buildInitialSelectionFromRow(runRow, false) : null
        resolvedInitialProjectRunSelection = resolvedRunSelection?.selection || null
        resolvedInitialStates = resolvedRunSelection?.states || []
        refUrlOwnedStateSelection.current = false
        resolvedInitialSelectionType = 'runId'
        resolvedInitialSelectionValue = initialRunIdToApply
      }

      const persistedProjectRunSelection =
        resolvedInitialProjectRunSelection ||
        readSelectionFromStorage(getStorageKey(PROJECT_SELECTION_STORAGE_KEY))
      const validPersistedProjectRunSelection = filterSelectionByAllowedKeys(
        persistedProjectRunSelection,
        allProjectRunKeys
      )
      const hasInitialProjectRunSelection =
        !!resolvedInitialProjectRunSelection &&
        Object.keys(resolvedInitialProjectRunSelection).length > 0
      const hasValidCheckedProjectRunSelection = Object.values(
        validPersistedProjectRunSelection
      ).some((value: any) => value?.checked)
      let projectRunSelection: any = {}
      const runs: string[] = []
      const selectedProjects: string[] = []
      const usingPersistedSelection = Object.keys(validPersistedProjectRunSelection).length > 0

      if (hasInitialProjectRunSelection && !hasValidCheckedProjectRunSelection) {
        setProjectRunNodes(projNodesData)
        setSelectedProjectRunKeys({} as any)
        setStateNodes([])
        setSelectedStateKeys(null)
        onReset && onReset()
        onInitialProjectRunSelectionRejected &&
          onInitialProjectRunSelectionRejected(
            resolvedInitialSelectionType,
            resolvedInitialSelectionValue
          )
        return
      } else if (
        (initialCaseIdToApply || initialRunIdToApply) &&
        !resolvedInitialProjectRunSelection
      ) {
        setProjectRunNodes(projNodesData)
        setSelectedProjectRunKeys({} as any)
        setStateNodes([])
        setSelectedStateKeys(null)
        onReset && onReset()
        onInitialProjectRunSelectionRejected &&
          onInitialProjectRunSelectionRejected(
            initialCaseIdToApply ? 'caseId' : 'runId',
            initialCaseIdToApply || initialRunIdToApply
          )
        return
      } else if (usingPersistedSelection) {
        projectRunSelection = validPersistedProjectRunSelection
        // Derive projects and runs from the restored selection
        Object.entries(projectRunSelection).forEach(([key, value]: [string, any]) => {
          if (value?.checked) {
            if (key.includes(KEY_DELIMITER)) {
              runs.push(key)
              const projectKey = key.split(KEY_DELIMITER)[0]
              if (!selectedProjects.includes(projectKey)) {
                selectedProjects.push(projectKey)
              }
            } else {
              if (!selectedProjects.includes(key)) {
                selectedProjects.push(key)
              }
            }
          }
        })
      } else {
        projNodesData.forEach(node => {
          const found = latestProjects.find(project => node.key.includes(project))
          if (found) {
            projectRunSelection[node.key] = { checked: true }
            selectedProjects.push(node.key)

            // also check all children if it's a project node
            if (!node.key.includes(KEY_DELIMITER) && node.children) {
              node.children.forEach(child => {
                projectRunSelection[child.key] = { checked: true }
                runs.push(child.key)
              })
            }
          }
        })
      }

      setProjectRunNodes(projNodesData)
      handleProjectRunSelectionChange({
        value: projectRunSelection,
        ignorePersistedStateSelection: !!resolvedInitialSelectionType,
        skipPersistProjectRunSelection: !!resolvedInitialSelectionType
      })
      if (resolvedInitialProjectRunSelection) {
        const initialStateKeys = (resolvedInitialStates || []).reduce(
          (acc: Record<string, { checked: boolean }>, state) => {
            acc[String(state)] = { checked: true }
            return acc
          },
          {}
        )
        setSelectedStateKeys(Object.keys(initialStateKeys).length > 0 ? initialStateKeys : null)
        onStateSelectionChanged(resolvedInitialStates || [], false)
      }
      if (selectedProjects.length > 0) {
        // Let parent component know the selection and load data
        onProjectRunSelectionChanged &&
          onProjectRunSelectionChanged(
            projectRunSelection,
            selectedProjects,
            true,
            runs,
            true,
            selectedProjects
          )
      }

      // get states for the selected projects/runs
      const states: Set<string> = new Set()
      Object.entries(projectRunSelection).forEach(([key, value]: [string, any]) => {
        if (value.checked) {
          if (key.includes(KEY_DELIMITER)) {
            // project node
            const parts = key.split(KEY_DELIMITER)
            const projectKey = parts[0]
            const runKey = parts[1]
            // find states for this run from raw data
            refRawProjectRunData.current.forEach(row => {
              if (row[0] === projectKey && row[1] === runKey) {
                states.add(row[2])
              }
            })
          }
        }
      })

      // make state tree nodes
      const stateNodeMap: Record<string, TreeNode> = {}
      states.forEach(state => {
        stateNodeMap[state] = {
          key: state,
          label: state
        }
      })
      const availableStateNodes = Object.values(stateNodeMap)
      setStateNodes(availableStateNodes)

      // State selection restoration/pruning is centralized in
      // handleProjectRunSelectionChange(...) to avoid duplicate
      // onStateSelectionChanged(...) calls during initial load.
    } catch (error: any) {
      console.error('Error fetching project/run data:', {
        error,
        errorCode: error?.code,
        errorMessage: error?.message,
        errorResponse: error?.response,
        statusCode: error?.response?.status,
        statusText: error?.response?.statusText,
        responseData: error?.response?.data,
        currentTeam
      })
      // Show user-friendly error message
      if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK') {
        toast.error('Network timeout loading projects. Please check your connection and refresh.')
      } else if (error?.response?.status === 401 || error?.response?.status === 403) {
        toast.error('Authentication error. Please log out and log back in.')
      } else if (error?.response?.status === 500 || error?.response?.status === 503) {
        toast.error('Server error loading project list. Please try again or contact support.')
      } else {
        toast.error('Error loading project list. Please refresh the page or contact support.')
      }
    } finally {
      onFilterLoadingChanged && onFilterLoadingChanged(false)
    }
  }

  useEffect(() => {
    getUsers()
      .then(engData => {
        if (engData.data) {
          const engs = protoService.decodeProtoResponse(engData.data, 'user')
          const userOptions = engs.map(user => {
            return {
              label: `${user.firstName} ${user.lastName}`,
              value: user._id
            }
          })
          setUserOptions(userOptions)
        }
      })
      .catch(err => {
        console.error(err)
        toast.error('Error loading engineers list.')
      })
    initData()
  }, [])

  useEffect(() => {
    if (refFirstLoad.current) {
      refFirstLoad.current = false
      return // Skip on first render
    }
    if (currentTeam) {
      // clear saved selection and local state
      setProjectRunNodes([])
      setStateNodes([])
      setBaselineNodes([])
      setSelectedProjectRunKeys(null)
      setSelectedStateKeys(null)
      setSelectedBaselineKeys(null)
      onReset && onReset()
      initData()
    }
  }, [currentTeam, selectedUserId, selectedArea, selectedCar])

  return (
    <div
      className="flex flex-col items-stretch w-full space-y-2 overflow-y-auto"
      style={{
        height: `calc(100vh - ${topSpacing + 200}px)`,
        maxHeight: `calc(100vh - ${topSpacing + 200}px)`,
        width: '320px'
      }}>
      {/* filter box*/}
      <div
        className={
          showFilter ? 'flex flex-col items-stretch border rounded-lg p-4 space-y-2' : 'hidden'
        }>
        <div className="font-bold">Filters</div>
        {filterContent}
        <div className="flex items-center">
          <Checkbox
            inputId="byMeOnly"
            pt={CheckboxStyle}
            onChange={handleShowMeOnlyChange}
            checked={byMeOnly}></Checkbox>
          <label htmlFor="byMeOnly" className="ml-2 font-normal">
            Submitted by Me Only
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <label className="font-normal basis-[90px] text-right">Submitted by:</label>
          <Dropdown
            filter
            showClear
            disabled={byMeOnly}
            loading={loadingUsers}
            value={selectedUserId}
            options={userOptions}
            onChange={e => {
              setSelectedUserId(e.value)
            }}
            placeholder="Select a user"
            pt={{
              root: {
                className: 'max-w-full basis-0 grow'
              },
              input: {
                className: '!p-2'
              },
              trigger: {
                className: '!w-8'
              },
              clearIcon: {
                className: '!right-8'
              }
            }}
          />
        </div>
        {showCarFilter && (
          <div className="flex items-center space-x-2">
            <label className="font-normal basis-[90px] text-right">Car:</label>
            <Dropdown
              filter
              showClear
              value={selectedCar}
              options={vehicles}
              dataKey="_id"
              optionLabel="name"
              optionValue="_id"
              onChange={e => {
                setSelectedCar(e.value)
              }}
              placeholder="Select a car"
              pt={{
                root: {
                  className: 'max-w-full basis-0 grow'
                },
                input: {
                  className: '!p-2'
                },
                trigger: {
                  className: '!w-8'
                },
                clearIcon: {
                  className: '!right-8'
                }
              }}
            />
          </div>
        )}
        {showAreaFilter && (
          <div className="flex items-center space-x-2">
            <label className="font-normal basis-[90px] text-right">Area:</label>
            <Dropdown
              filter
              showClear
              value={selectedArea}
              options={areas}
              optionLabel="label"
              optionValue="value"
              onChange={e => {
                setSelectedArea(e.value)
              }}
              placeholder="Select an area"
              pt={{
                root: {
                  className: 'max-w-full basis-0 grow'
                },
                input: {
                  className: '!p-2'
                },
                trigger: {
                  className: '!w-8'
                },
                clearIcon: {
                  className: '!right-8'
                }
              }}
            />
          </div>
        )}
      </div>
      <div
        className="basis-0 grow shrink-0 flex flex-col items-stretch space-y-2 p-4 !border border-solid rounded-lg "
        style={treeHeightClass}>
        <div className="flex items-center justify-between">
          <div className="font-semibold text-left text-gm-blue-dark dark:text-neutral-20">
            Project - Run
          </div>
          <div className="flex items-center space-x-4">
            <button
              type="button"
              disabled={projectRunLoading || loadingHeadlineData || projectRunNodes.length === 0}
              onClick={handleSelectAllProjectRuns}
              className="text-xs text-gm-blue disabled:text-neutral-400 hover:underline disabled:no-underline">
              Select All
            </button>
            <button
              type="button"
              disabled={projectRunLoading || loadingHeadlineData}
              onClick={handleDeselectAllProjectRuns}
              className="text-xs text-gm-blue disabled:text-neutral-400 hover:underline disabled:no-underline">
              Deselect All
            </button>
          </div>
        </div>
        <Tree
          value={projectRunNodes}
          expandedKeys={expandedProjectRunKeys}
          onToggle={e => setExpandedProjectRunKeys(e.value || {})}
          disabled={projectRunLoading || loadingHeadlineData}
          emptyMessage={() => {
            return projectRunLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <i className="pi pi-spin pi-spinner text-xl text-gm-blue"></i>
                <div>Loading...</div>
              </div>
            ) : (
              'No projects/runs available.'
            )
          }}
          selectionKeys={selectedProjectRunKeys}
          onSelectionChange={handleProjectRunSelectionChange}
          selectionMode="checkbox"
          className="h-full border-0 checkbox-tree"
          onSelect={handleSelection}
          onUnselect={handleSelection}
          togglerTemplate={togglerTemplate}
          nodeTemplate={projectRunNodeTemplate}
          style={{ minHeight: 'calc(100% - 30px)', maxHeight: 'calc(100% - 30px)' }}
          pt={{
            root: {
              className: '!p-0 basis-0 grow !bg-transparent'
            },
            content: {
              className: '!p-0 min-w-0'
            },
            node: {
              className: '!p-0'
            },
            // Preset sizes the checkbox root at w-6 while App.css shrinks the box to 18px,
            // leaving dead space before the label.
            nodeCheckbox: {
              className: '!w-[18px] mr-1'
            },
            label: {
              className: 'text-xs min-w-0 flex-1 overflow-hidden'
            },
            input: {
              className: 'w-4 h-4'
            },
            container: {
              className: 'overflow-y-auto overflow-x-hidden h-full'
            }
          }}
        />
      </div>
      <div
        className="flex flex-col items-stretch shrink-0 space-y-2 p-4 !border border-solid rounded-lg"
        style={treeHeightClass}>
        <div className="font-semibold text-left text-gm-blue-dark dark:text-neutral-20">States</div>
        <Tree
          value={stateNodes}
          disabled={projectRunLoading || loadingHeadlineData}
          selectionKeys={selectedStateKeys}
          onSelectionChange={handleStateSelectionChange}
          selectionMode="checkbox"
          className="h-full border-0 checkbox-tree "
          togglerTemplate={togglerTemplate}
          style={{ minHeight: 'calc(100% - 30px)', maxHeight: 'calc(100% - 30px)' }}
          pt={oneLevelTreeStyle}
        />
      </div>
      <div
        className="flex flex-col items-stretch shrink-0 space-y-2 p-4 !border border-solid rounded-lg"
        style={treeHeightClass}>
        <div className="font-semibold text-left text-gm-blue-dark dark:text-neutral-20">
          Baselines
        </div>
        <Tree
          value={baselineNodes}
          disabled={projectRunLoading || loadingHeadlineData}
          emptyMessage={() => {
            return 'No baselines available for current project/run and state filters.'
          }}
          selectionKeys={selectedBaselineKeys}
          onSelectionChange={handleBaselineSelectionChange}
          selectionMode="checkbox"
          className="h-full border-0 checkbox-tree"
          togglerTemplate={togglerTemplate}
          style={{ minHeight: 'calc(100% - 30px)', maxHeight: 'calc(100% - 30px)' }}
          pt={oneLevelTreeStyle}
        />
      </div>
    </div>
  )
}

export default ProjectRunSelector
